import { Component, EventEmitter, Output, signal, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { RightPanelHeaderComponent } from '../../../../components/right-panel-header/right-panel-header.component';
import { FormSelectDropdownComponent } from '../../../../components/form-select-dropdown/form-select-dropdown.component';
import { VersionDropdownComponent, VersionOption } from './version-dropdown/version-dropdown.component';
import { DatasetDropdownComponent, DatasetOption } from './dataset-dropdown/dataset-dropdown.component';
import { DropdownObjectType } from '../../../../shared/types';
import { SharedDataService } from '../../../../services/shared-data.service';
import { TaxonomyApiService } from '../../../../services/api-services/taxonomy-api.service';
import { NotificationService } from '../../../../services/notification.service';

@Component({
  selector: 'app-playground-test-form',
  standalone: true,
  imports: [CommonModule, FormsModule, RightPanelHeaderComponent, FormSelectDropdownComponent, VersionDropdownComponent, DatasetDropdownComponent],
  templateUrl: './playground-test-form.component.html',
  styleUrl: './playground-test-form.component.scss'
})
export class PlaygroundTestFormComponent implements OnInit {
  @Output() testFormClosed = new EventEmitter<void>();
  @Output() testCreated = new EventEmitter<{ taxonomyId: string; testId: string }>();

  private taxonomyApiService = inject(TaxonomyApiService);
  private notificationService = inject(NotificationService);

  // Form data signals
  testLabel = signal<string>('');
  selectedTaxonomy = signal<DropdownObjectType | null>(null);
  selectedVersion = signal<VersionOption | null>(null);
  selectedDataset = signal<DatasetOption | null>(null);

  // Validation signals
  isTestLabelValid = signal<boolean>(false);
  isTaxonomySelected = signal<boolean>(false);
  isVersionSelected = signal<boolean>(false);
  isDatasetSelected = signal<boolean>(false);

  // Loading states
  isLoadingTaxonomies = signal<boolean>(false);
  isLoadingVersions = signal<boolean>(false);
  isLoadingDatasets = signal<boolean>(false);
  isCreatingTest = signal<boolean>(false);

  // API data
  taxonomyOptions: DropdownObjectType[] = [];
  versionOptions: VersionOption[] = [];
  datasetOptions: DatasetOption[] = [];

  constructor(
    private router: Router,
    private sharedDataService: SharedDataService
  ) {}

  ngOnInit() {
    this.validateTestLabel('');
    this.loadTaxonomies();
    this.loadDatasets();
  }

  // Load taxonomies from API
  loadTaxonomies() {
    this.isLoadingTaxonomies.set(true);
    this.taxonomyApiService.getTaxonomyV2({}).subscribe({
      next: (response: any) => {
        if (response?.data) {
          this.taxonomyOptions = response.data.map((taxonomy: any) => ({
            label: taxonomy.taxonomy_name,
            value: taxonomy.taxonomy_id,
            type: 'taxonomy'
          }));
        }
        this.isLoadingTaxonomies.set(false);
      },
      error: (error) => {
        console.error('Failed to load taxonomies:', error);
        this.notificationService.error('Error', 'Failed to load taxonomies');
        this.isLoadingTaxonomies.set(false);
      }
    });
  }

  // Load versions for selected taxonomy
  loadVersions(taxonomyId: string) {
    this.isLoadingVersions.set(true);
    this.versionOptions = [];
    this.selectedVersion.set(null);
    this.isVersionSelected.set(false);

    this.taxonomyApiService.getVersionHistoryById({ taxonomy_id: taxonomyId }).subscribe({
      next: (response: any) => {
        if (response?.data?.version_history_details) {
          this.versionOptions = response.data.version_history_details;
        }
        this.isLoadingVersions.set(false);
      },
      error: (error) => {
        console.error('Failed to load versions:', error);
        this.notificationService.error('Error', 'Failed to load versions');
        this.isLoadingVersions.set(false);
      }
    });
  }

  // Load datasets from API
  loadDatasets() {
    this.isLoadingDatasets.set(true);
    this.taxonomyApiService.getDatasetsByUser().subscribe({
      next: (response: any) => {
        if (response?.data) {
          this.datasetOptions = response.data.map((dataset: any) => ({
            label: dataset.DatasetLabel,
            value: dataset.DatasetId,
            type: 'dataset'
          }));
        }
        this.isLoadingDatasets.set(false);
      },
      error: (error) => {
        console.error('Failed to load datasets:', error);
        this.notificationService.error('Error', 'Failed to load datasets');
        this.isLoadingDatasets.set(false);
      }
    });
  }

  // Form validation methods
  onTestLabelChange(value: string) {
    this.testLabel.set(value);
    this.validateTestLabel(value);
  }

  onTaxonomySelect(item: DropdownObjectType) {
    this.selectedTaxonomy.set(item);
    this.isTaxonomySelected.set(true);

    // Clear previous version selection
    this.selectedVersion.set(null);
    this.isVersionSelected.set(false);

    // Load versions for selected taxonomy
    if (item.value) {
      this.loadVersions(item.value);
    }
  }

  onVersionSelect(item: VersionOption) {
    this.selectedVersion.set(item);
    this.isVersionSelected.set(true);
  }

  onDatasetSelect(item: DatasetOption) {
    this.selectedDataset.set(item);
    this.isDatasetSelected.set(true);
  }

  private validateTestLabel(value: string) {
    this.isTestLabelValid.set(value.trim().length > 0);
  }

  isFormValid(): boolean {
    return this.isTestLabelValid() && this.isTaxonomySelected() && this.isVersionSelected() && this.isDatasetSelected();
  }

  // Form actions
  onConfirm() {
    if (this.isFormValid()) {
      this.createTest();
    }
  }

  // Create test API call
  private createTest() {
    if (!this.selectedTaxonomy() || !this.selectedVersion() || !this.selectedDataset()) {
      return;
    }

    this.isCreatingTest.set(true);

    const payload = {
      taxonomy_id: this.selectedTaxonomy()!.value,
      record_id: this.selectedVersion()!.record_id,
      label: this.testLabel().trim(),
      dataset_id: this.selectedDataset()!.value
    };

    this.taxonomyApiService.createTest(payload).subscribe({
      next: (response: any) => {
        this.notificationService.processing('Success', 'Test in Progress. Please wait');

        // Emit the test created event with taxonomy ID and test ID
        if (response?.data?.batch_id) {
          this.testCreated.emit({
            taxonomyId: this.selectedTaxonomy()!.value,
            testId: response.data.batch_id
          });
        }

        this.onCancel();
        this.isCreatingTest.set(false);
      },
      error: (error) => {
        console.error('Failed to create test:', error);
        this.notificationService.error('Error', 'Failed to create test');
        this.isCreatingTest.set(false);
      }
    });
  }

  onCancel() {
    this.testFormClosed.emit();
  }

  onOutsideClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  // Navigation to data view
  goToDataView() {
    // Close the test form first
    this.onCancel();

    // Update navbar data to highlight Insights Dashboard
    this.sharedDataService.updateDashboardState('navBarData', {
      routeLink: 'conversation-insights',
      icon: 'assets/icons/bar_chart_icon.svg',
      label: 'Insights Dashboard'
    });

    // Navigate to insights dashboard
    this.router.navigate(['/conversation-insights']);

    // Set the dashboard state to show data view tab
    setTimeout(() => {
      this.sharedDataService.setTableViewFlagStatus(true);
      this.sharedDataService.updateDashboardState('tableViewFlag', true);
    }, 100);
  }
}
